import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { publicApi } from '../../services/publicApi'
import BookCard from './BookCard'

// A single curated list rendered as a horizontal row of book cards,
// mirroring the mobile app home (UPPERCASE title + blue "See all").
const ListRow = ({ list, index = 0 }) => {
  const books = Array.isArray(list.books) ? list.books : []
  if (books.length === 0) return null

  return (
    <section className="mb-12 rounded-lg border border-gray-100 bg-bg-surface p-4 shadow-soft sm:p-5">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-primary-700">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50">
              {list.icon || index + 1}
            </span>
            By Bukito
          </div>
          <h3 className="text-xl font-extrabold text-text-primary sm:text-2xl">
            {list.name}
          </h3>
          {list.description && (
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-text-secondary">
              {list.description}
            </p>
          )}
        </div>
        <Link
          to={`/list/${list.id}`}
          className="inline-flex h-10 shrink-0 items-center gap-1 rounded-lg bg-primary-50 px-3 text-sm font-bold text-primary-700 transition-colors hover:bg-primary-100"
        >
          See all
          <ChevronRight size={16} />
        </Link>
      </div>

      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        {books.map((book) => (
          <BookCard key={book.id ?? book.isbn13} book={book} />
        ))}
        {list.total_books > books.length && (
          <Link
            to={`/list/${list.id}`}
            className="flex aspect-[2/3] w-32 shrink-0 items-center justify-center rounded-lg bg-bg-soft text-primary-700 transition-colors hover:bg-primary-100 sm:w-36"
          >
            <span className="flex flex-col items-center gap-1 text-sm font-semibold">
              <ChevronRight size={24} />
              View all
            </span>
          </Link>
        )}
      </div>
    </section>
  )
}

const CuratedFeed = ({ header = 'full', showPanel = false, limit, sort }) => {
  const [lists, setLists] = useState([])
  const [status, setStatus] = useState('loading') // loading | ready | error

  useEffect(() => {
    let active = true
    publicApi
      .getCuratedLists({ limit, sort })
      .then((data) => {
        if (!active) return
        setLists(data)
        setStatus('ready')
      })
      .catch(() => {
        if (active) setStatus('error')
      })
    return () => {
      active = false
    }
  }, [limit, sort])

  const isEmpty = status === 'ready' && lists.length === 0
  const isCompactHeader = header === 'compact'
  const isHeaderHidden = header === 'none'

  return (
    <section className={`${isCompactHeader ? 'py-12 lg:py-16' : isHeaderHidden ? 'py-8 lg:py-10' : 'py-16 lg:py-20'} bg-bg-main`}>
      <div className="container-custom">
        {header === 'full' && (
          <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="mb-3 text-sm font-bold uppercase text-primary-700">Browse by mood and stage</p>
              <h2 className="text-3xl font-extrabold text-text-primary sm:text-4xl">
                Curated reading lists for ages 0-8
              </h2>
              <p className="mt-3 max-w-2xl text-lg leading-relaxed text-text-secondary">
                Handpicked by Bukito for bedtime, first readers, rainy afternoons and every small reading ritual in between.
              </p>
            </div>
            <Link
              to="/lists"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary-500 px-5 text-sm font-bold text-white shadow-button transition-colors hover:bg-primary-600"
            >
              Explore all lists
              <ArrowRight size={17} />
            </Link>
          </div>
        )}

        {isCompactHeader && (
          <div className="mb-8 rounded-lg border border-primary-100 bg-primary-50 p-5 sm:p-6">
            <p className="text-sm font-bold uppercase text-primary-700">Fresh from the app</p>
            <h2 className="mt-2 text-2xl font-extrabold text-text-primary sm:text-3xl">
              Start with a list, then save favourites in Bukito.
            </h2>
          </div>
        )}

        {(status === 'error' || isEmpty) && (
          <div className="rounded-lg border border-gray-100 bg-bg-surface py-16 text-center text-text-secondary shadow-soft">
            {status === 'error'
              ? 'Could not load the lists right now. Please try again in a moment.'
              : 'No lists are available yet. Check back soon.'}
          </div>
        )}

        {status === 'loading' ? (
          <div className="space-y-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-gray-100 bg-bg-surface p-5 shadow-soft">
                <div className="mb-4 h-5 w-48 animate-pulse rounded bg-bg-soft" />
                <div className="flex gap-4 overflow-hidden">
                  {[0, 1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="w-32 sm:w-36 shrink-0">
                      <div className="aspect-[2/3] animate-pulse rounded-lg bg-bg-soft" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={showPanel ? 'rounded-lg bg-bg-soft/60 p-3 sm:p-4' : ''}>
            {lists.map((list, index) => <ListRow key={list.id} list={list} index={index} />)}
          </div>
        )}
      </div>
    </section>
  )
}

export default CuratedFeed
